/**
 * ACT-3188: In the documents list, when the run-on-booking flag is ON,
 * show EXPIRING background checks (Checkr) as APPROVED to avoid the expiring pill.
 */
import {
  RequirementWorkflowType,
  WorkerRequirementMappingLevel,
} from "@clipboard-health/contract-worker-app-bff";
import { isNil } from "@clipboard-health/util-ts";
import { RootPaths } from "@src/appV2/App/paths";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { intersection } from "lodash";
import { generatePath, useHistory } from "react-router-dom";

import { type WorkerRequirement } from "../api/types";
import { DocumentOverviewCard } from "../components/OverviewCard";
import { getCheckrRequirementStatus } from "../Details/Workflows/Checkr/getCheckrRequirementStatus";
import { DOCUMENT_DETAILS_PATH, DOCUMENTS_BASE_PATH } from "../paths";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";
import { getApplicableWorkplacesDetails } from "../utils/getApplicableWorkplacesDetails";
import { isRequirementInstantReview } from "../utils/isRequirementInstantReview";
import { getCustomDocumentStatusCardContent } from "../utils/workerRequirementStatusContent";
import { WORKER_REQUIREMENT_STATUS_PILL_VARIANTS } from "./constants";

interface DocumentsPageDocumentCardsProps {
  documents?: WorkerRequirement[];
}

export function DocumentsPageDocumentCards(props: DocumentsPageDocumentCardsProps) {
  const { documents } = props;

  const worker = useDefinedWorker();
  const history = useHistory();
  const { licenses: availableLicenses } = useGetWorkerLicensesAllowedForDocuments();
  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  if (isNil(documents)) {
    return null;
  }

  return (
    <>
      {documents.map((requirement) => {
        const requirementStatus =
          requirement.requirement?.attributes.requirementWorkflowType ===
          RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
            ? getCheckrRequirementStatus({
                isRunBackgroundCheckOnBookingEnabled,
                status: requirement.status,
                isValid: requirement.isValid,
              })
            : requirement.status;

        const documentStatusPillVariant =
          WORKER_REQUIREMENT_STATUS_PILL_VARIANTS[requirementStatus];

        const { pillLabel: documentStatusPillLabel } = getCustomDocumentStatusCardContent({
          isRunBackgroundCheckOnBookingEnabled,
          requirementWorkflowType: requirement.requirement?.attributes.requirementWorkflowType,
          status: requirementStatus,
        });

        return (
          <DocumentOverviewCard
            key={requirement.requirementId}
            // todo: fix contract in worker bff to not be optional
            requirementName={requirement.requirement?.attributes.name ?? ""}
            documentStatusPillVariant={documentStatusPillVariant}
            documentStatusPillLabel={documentStatusPillLabel}
            qualifications={
              availableLicenses.length > 1
                ? intersection(requirement.qualifications ?? [], availableLicenses)
                : []
            }
            applicableWorkplacesDetails={
              requirement.level === WorkerRequirementMappingLevel.HCF
                ? getApplicableWorkplacesDetails({
                    workerRequirement: requirement,
                    workerAddress: worker.address,
                  })
                : undefined
            }
            isQuickSign={isRequirementInstantReview({
              workerRequirement: requirement,
            })}
            expiryDate={requirement.expiry ? parseISO(requirement.expiry) : undefined}
            onClick={() => {
              history.push(
                generatePath(
                  `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}`,
                  {
                    requirementId: requirement.requirementId,
                  }
                )
              );
            }}
          />
        );
      })}
    </>
  );
}
